import React, { useState, createElement } from 'react';
import { ArrowLeftIcon, CheckCircleIcon, DownloadIcon, HomeIcon } from 'lucide-react';
export function Results({
  team,
  phaseSummaries,
  startupDetails,
  onSaveResults,
  onRestart
}) {
  const [activeTab, setActiveTab] = useState('summary');
  const [editedDetails, setEditedDetails] = useState({
    name: startupDetails.name || 'Our Startup',
    description: startupDetails.description || 'A brief description of our startup idea.',
    logo: startupDetails.logo || '🚀',
    journey: startupDetails.journey || 'Our journey begins with identifying a problem and creating a solution.',
    resources: startupDetails.resources || ['Funding', 'Technical expertise', 'Marketing support'],
    mentors: startupDetails.mentors || ['Industry expert', 'Technical advisor', 'Business coach'],
    deliverables: startupDetails.deliverables || ['MVP', 'Customer validation', 'Business plan'],
    timeline: startupDetails.timeline || ['Month 1: Research', 'Month 3: Prototype', 'Month 6: Launch']
  });
  const handleInputChange = (field, value) => {
    setEditedDetails({
      ...editedDetails,
      [field]: value
    });
  };
  const handleArrayInputChange = (field, index, value) => {
    const newArray = [...editedDetails[field]];
    newArray[index] = value;
    setEditedDetails({
      ...editedDetails,
      [field]: newArray
    });
  };
  const addArrayItem = field => {
    setEditedDetails({
      ...editedDetails,
      [field]: [...editedDetails[field], '']
    });
  };
  const removeArrayItem = (field, index) => {
    const newArray = [...editedDetails[field]];
    newArray.splice(index, 1);
    setEditedDetails({
      ...editedDetails,
      [field]: newArray
    });
  };
  const saveResults = () => {
    onSaveResults(editedDetails);
  };
  const downloadResults = () => {
    // Create a text version of the results
    let resultsText = `# ${editedDetails.name} - Startup Plan\n\n`;
    resultsText += `## Team Members\n`;
    team.forEach(member => {
      resultsText += `- ${member.name} (${member.role || 'Team Member'})\n`;
    });
    resultsText += `\n## Startup Description\n${editedDetails.description}\n\n`;
    resultsText += `## Our Journey\n${editedDetails.journey}\n\n`;
    resultsText += `## Resources Needed\n`;
    editedDetails.resources.forEach(resource => {
      resultsText += `- ${resource}\n`;
    });
    resultsText += `\n## Mentors & Advisors\n`;
    editedDetails.mentors.forEach(mentor => {
      resultsText += `- ${mentor}\n`;
    });
    resultsText += `\n## Key Deliverables\n`;
    editedDetails.deliverables.forEach(deliverable => {
      resultsText += `- ${deliverable}\n`;
    });
    resultsText += `\n## Timeline\n`;
    editedDetails.timeline.forEach(milestone => {
      resultsText += `- ${milestone}\n`;
    });
    resultsText += `\n## Phase Summaries\n`;
    Object.keys(phaseSummaries).forEach(phaseId => {
      resultsText += `### Phase ${phaseId}\n${phaseSummaries[phaseId]}\n\n`;
    });
    // Create a download link
    const blob = new Blob([resultsText], {
      type: 'text/plain'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${editedDetails.name.replace(/\s+/g, '-').toLowerCase()}-startup-plan.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  return <div className="w-full min-h-screen p-4 md:p-6" data-id="element-8">
      <header className="mb-6" data-id="element-9">
        <h1 className="text-3xl font-bold text-indigo-800 text-center" data-id="element-10">
          Bridges of Life
        </h1>
        <p className="text-center text-indigo-600" data-id="element-11">
          Your Startup Journey Results
        </p>
      </header>
      <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6" data-id="element-12">
        <div className="border-b border-gray-200" data-id="element-13">
          <nav className="flex" data-id="element-14">
            <button className={`px-4 py-3 text-sm font-medium ${activeTab === 'summary' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('summary')} data-id="element-15">
              Summary
            </button>
            <button className={`px-4 py-3 text-sm font-medium ${activeTab === 'details' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('details')} data-id="element-16">
              Startup Details
            </button>
            <button className={`px-4 py-3 text-sm font-medium ${activeTab === 'team' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('team')} data-id="element-17">
              Team
            </button>
            <button className={`px-4 py-3 text-sm font-medium ${activeTab === 'journey' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('journey')} data-id="element-18">
              Journey
            </button>
          </nav>
        </div>
        <div className="p-6" data-id="element-19">
          {activeTab === 'summary' && <div data-id="element-20">
              <div className="flex items-center justify-center mb-6" data-id="element-21">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center text-3xl" data-id="element-22">
                  {editedDetails.logo}
                </div>
              </div>
              <h2 className="text-2xl font-bold text-center text-indigo-800 mb-2" data-id="element-23">
                {editedDetails.name}
              </h2>
              <p className="text-center text-gray-600 mb-6 max-w-2xl mx-auto" data-id="element-24">
                {editedDetails.description}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-25">
                <div className="bg-indigo-50 rounded-lg p-4" data-id="element-26">
                  <h3 className="text-lg font-medium text-indigo-800 mb-3" data-id="element-27">
                    Team Members
                  </h3>
                  <div className="space-y-2" data-id="element-28">
                    {team.map(member => <div key={member.id} className="flex items-center" data-id="element-29">
                        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-sm mr-2" data-id="element-30">
                          {member.avatar}
                        </div>
                        <div data-id="element-31">
                          <p className="font-medium" data-id="element-32">{member.name}</p>
                          <p className="text-xs text-gray-500" data-id="element-33">
                            {member.role || 'Team Member'}
                          </p>
                        </div>
                      </div>)}
                  </div>
                </div>
                <div className="bg-indigo-50 rounded-lg p-4" data-id="element-34">
                  <h3 className="text-lg font-medium text-indigo-800 mb-3" data-id="element-35">
                    Key Deliverables
                  </h3>
                  <ul className="space-y-1" data-id="element-36">
                    {editedDetails.deliverables.map((deliverable, index) => <li key={index} className="flex items-start" data-id="element-37">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" data-id="element-38" />
                        <span data-id="element-39">{deliverable}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
              <div className="flex justify-center space-x-4 mt-8" data-id="element-40">
                <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition flex items-center" onClick={onRestart} data-id="element-41">
                  <HomeIcon className="w-4 h-4 mr-2" data-id="element-42" />
                  Return to Lobby
                </button>
                <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition flex items-center" onClick={downloadResults} data-id="element-43">
                  <DownloadIcon className="w-4 h-4 mr-2" data-id="element-44" />
                  Download Results
                </button>
              </div>
            </div>}
          {activeTab === 'details' && <div data-id="element-45">
              <h2 className="text-xl font-semibold mb-4" data-id="element-46">Startup Details</h2>
              <div className="space-y-4 mb-6" data-id="element-47">
                <div data-id="element-48">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-49">
                    Startup Name
                  </label>
                  <input type="text" className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:outline-none" value={editedDetails.name} onChange={e => handleInputChange('name', e.target.value)} data-id="element-50" />
                </div>
                <div data-id="element-51">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-52">
                    Logo/Emoji
                  </label>
                  <input type="text" className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:outline-none" value={editedDetails.logo} onChange={e => handleInputChange('logo', e.target.value)} placeholder="Use an emoji or text" data-id="element-53" />
                </div>
                <div data-id="element-54">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-55">
                    Description
                  </label>
                  <textarea className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:outline-none" rows={3} value={editedDetails.description} onChange={e => handleInputChange('description', e.target.value)} data-id="element-56"></textarea>
                </div>
              </div>
              <div className="flex justify-end" data-id="element-57">
                <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition" onClick={saveResults} data-id="element-58">
                  Save Details
                </button>
              </div>
            </div>}
          {activeTab === 'team' && <div data-id="element-59">
              <h2 className="text-xl font-semibold mb-4" data-id="element-60">Team Members</h2>
              <div className="space-y-4 mb-6" data-id="element-61">
                {team.map(member => <div key={member.id} className="bg-gray-50 rounded-lg p-4" data-id="element-62">
                    <div className="flex items-center mb-3" data-id="element-63">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-xl mr-3" data-id="element-64">
                        {member.avatar}
                      </div>
                      <div data-id="element-65">
                        <h3 className="font-medium" data-id="element-66">{member.name}</h3>
                        <p className="text-sm text-gray-500" data-id="element-67">
                          {member.isCurrentUser ? 'You' : 'Team Member'}
                        </p>
                      </div>
                    </div>
                    <div data-id="element-68">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-69">
                        Role in Startup
                      </label>
                      <input type="text" className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:outline-none" placeholder="e.g., CEO, CTO, Designer, Developer" value={member.role} onChange={e => {
                  // Update team member role
                  // Note: In a real app, you'd update this in state
                }} data-id="element-70" />
                    </div>
                  </div>)}
              </div>
            </div>}
          {activeTab === 'journey' && <div data-id="element-71">
              <h2 className="text-xl font-semibold mb-4" data-id="element-72">Startup Journey</h2>
              <div className="space-y-6" data-id="element-73">
                <div data-id="element-74">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-75">
                    Journey Description
                  </label>
                  <textarea className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:outline-none" rows={4} value={editedDetails.journey} onChange={e => handleInputChange('journey', e.target.value)} data-id="element-76"></textarea>
                </div>
                <div data-id="element-77">
                  <div className="flex justify-between items-center mb-2" data-id="element-78">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-79">
                      Resources Needed
                    </label>
                    <button className="text-sm text-indigo-600 hover:text-indigo-800" onClick={() => addArrayItem('resources')} data-id="element-80">
                      + Add Resource
                    </button>
                  </div>
                  {editedDetails.resources.map((resource, index) => <div key={index} className="flex mb-2" data-id="element-81">
                      <input type="text" className="flex-1 p-2 border border-gray-300 rounded-l-md focus:ring-2 focus:ring-indigo-500 focus:outline-none" value={resource} onChange={e => handleArrayInputChange('resources', index, e.target.value)} data-id="element-82" />
                      <button className="px-3 bg-gray-100 border border-l-0 border-gray-300 rounded-r-md hover:bg-gray-200" onClick={() => removeArrayItem('resources', index)} data-id="element-83">
                        ×
                      </button>
                    </div>)}
                </div>
                <div data-id="element-84">
                  <div className="flex justify-between items-center mb-2" data-id="element-85">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-86">
                      Mentors & Advisors
                    </label>
                    <button className="text-sm text-indigo-600 hover:text-indigo-800" onClick={() => addArrayItem('mentors')} data-id="element-87">
                      + Add Mentor
                    </button>
                  </div>
                  {editedDetails.mentors.map((mentor, index) => <div key={index} className="flex mb-2" data-id="element-88">
                      <input type="text" className="flex-1 p-2 border border-gray-300 rounded-l-md focus:ring-2 focus:ring-indigo-500 focus:outline-none" value={mentor} onChange={e => handleArrayInputChange('mentors', index, e.target.value)} data-id="element-89" />
                      <button className="px-3 bg-gray-100 border border-l-0 border-gray-300 rounded-r-md hover:bg-gray-200" onClick={() => removeArrayItem('mentors', index)} data-id="element-90">
                        ×
                      </button>
                    </div>)}
                </div>
                <div data-id="element-91">
                  <div className="flex justify-between items-center mb-2" data-id="element-92">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-93">
                      Key Deliverables
                    </label>
                    <button className="text-sm text-indigo-600 hover:text-indigo-800" onClick={() => addArrayItem('deliverables')} data-id="element-94">
                      + Add Deliverable
                    </button>
                  </div>
                  {editedDetails.deliverables.map((deliverable, index) => <div key={index} className="flex mb-2" data-id="element-95">
                      <input type="text" className="flex-1 p-2 border border-gray-300 rounded-l-md focus:ring-2 focus:ring-indigo-500 focus:outline-none" value={deliverable} onChange={e => handleArrayInputChange('deliverables', index, e.target.value)} data-id="element-96" />
                      <button className="px-3 bg-gray-100 border border-l-0 border-gray-300 rounded-r-md hover:bg-gray-200" onClick={() => removeArrayItem('deliverables', index)} data-id="element-97">
                        ×
                      </button>
                    </div>)}
                </div>
                <div data-id="element-98">
                  <div className="flex justify-between items-center mb-2" data-id="element-99">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-100">
                      Timeline
                    </label>
                    <button className="text-sm text-indigo-600 hover:text-indigo-800" onClick={() => addArrayItem('timeline')} data-id="element-101">
                      + Add Milestone
                    </button>
                  </div>
                  {editedDetails.timeline.map((milestone, index) => <div key={index} className="flex mb-2" data-id="element-102">
                      <input type="text" className="flex-1 p-2 border border-gray-300 rounded-l-md focus:ring-2 focus:ring-indigo-500 focus:outline-none" value={milestone} onChange={e => handleArrayInputChange('timeline', index, e.target.value)} data-id="element-103" />
                      <button className="px-3 bg-gray-100 border border-l-0 border-gray-300 rounded-r-md hover:bg-gray-200" onClick={() => removeArrayItem('timeline', index)} data-id="element-104">
                        ×
                      </button>
                    </div>)}
                </div>
              </div>
              <div className="flex justify-end mt-6" data-id="element-105">
                <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition" onClick={saveResults} data-id="element-106">
                  Save Journey Details
                </button>
              </div>
            </div>}
        </div>
      </div>
      <div className="flex justify-center" data-id="element-107">
        <button className="px-4 py-2 flex items-center text-indigo-600 hover:text-indigo-800" onClick={onRestart} data-id="element-108">
          <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-109" />
          Return to Lobby
        </button>
      </div>
    </div>;
}